/**
 * Lightweight email validation tuned to catch common typos that pass a basic
 * shape check. Returns `null` when valid, or a human-readable error message.
 *
 * Verbatim port from `_prototype/lib/validateEmail.ts`.
 */

const BASIC_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INVALID_TLDS = new Set([
  "con", "cmo", "comm", "vom", "xom", "cim",
  "ney", "nte", "neet",
  "orgg", "ogr", "rog",
  "iio", "ioo",
]);

const DOMAIN_TYPOS: Record<string, string> = {
  gamil: "gmail", gmai: "gmail", gmial: "gmail", gmali: "gmail", gnail: "gmail",
  gmaill: "gmail", gamail: "gmail", gmil: "gmail",
  yaho: "yahoo", yahooo: "yahoo", yahho: "yahoo", yhaoo: "yahoo",
  hotmial: "hotmail", hotmal: "hotmail", hotmaill: "hotmail",
  hormail: "hotmail", hotnail: "hotmail",
  outloo: "outlook", outllok: "outlook", outlok: "outlook", outook: "outlook",
  iclod: "icloud", iclud: "icloud", ikloud: "icloud", iclou: "icloud",
};

export function isValidEmail(email: string): boolean {
  return validateEmail(email) === null;
}

export function validateEmail(email: string): string | null {
  const trimmed = email.trim();
  if (!trimmed) return null;

  if (!BASIC_SHAPE.test(trimmed)) {
    return "Please enter a valid email address";
  }

  const at = trimmed.lastIndexOf("@");
  const domain = trimmed.slice(at + 1).toLowerCase();
  const parts = domain.split(".");
  const tld = parts[parts.length - 1]!;
  const domainName = parts.slice(0, -1).join(".");

  if (INVALID_TLDS.has(tld)) {
    const suggestion = guessTldFix(tld);
    if (suggestion) return `Did you mean .${suggestion}?`;
    return `“.${tld}” doesn't look like a real domain`;
  }
  if (DOMAIN_TYPOS[domainName]) {
    return `Did you mean ${DOMAIN_TYPOS[domainName]}.${tld}?`;
  }
  if (parts.some((p) => p.length === 0)) {
    return "Please enter a valid email address";
  }
  return null;
}

function guessTldFix(badTld: string): string | null {
  if (["con", "cmo", "comm", "vom", "xom", "cim"].includes(badTld)) return "com";
  if (["ney", "nte", "neet"].includes(badTld)) return "net";
  if (["orgg", "ogr", "rog"].includes(badTld)) return "org";
  if (["iio", "ioo"].includes(badTld)) return "io";
  return null;
}
